import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { CalendarCheck, Clock, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { FadeUp } from "./motion";

const inputCls = "w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300";

/** Parcours complet de prise de rendez-vous — réutilisé par la page /rendez-vous
    ET la page Contact (demande du propriétaire, 2026-10-20 : le formulaire de
    réservation REMPLACE le formulaire de message sur la page Contact). */
export default function RendezvousFormulaire() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // TYPE LIBRE (demande du propriétaire, 2026-10-20) : le client écrit lui-même
  // son type de rendez-vous dans une case vide (ex. : visite en boutique, appel
  // téléphonique…) — plus de choix imposés.
  const [typeLabel, setTypeLabel] = useState("");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null); // null=pas chargé, []=fermé/complet
  const [slotIso, setSlotIso] = useState("");
  const [duree, setDuree] = useState(15); // minutes — réglable par type dans Paramètres
  // Préremplissage depuis une fiche produit (grande quantité / demande d'estimé) :
  // le formulaire de message ayant été remplacé par la réservation, le contexte
  // produit arrive dans la zone Sujet (demande du propriétaire)
  const [sujet, setSujet] = useState(() => {
    if (searchParams.get("produit")) {
      const qte = searchParams.get("qte") || "";
      return lang === "en"
        ? `Quote request — ${searchParams.get("produit")}${qte ? ` — quantity: ${qte}` : ""}`
        : `Demande d'estimé — ${searchParams.get("produit")}${qte ? ` — quantité : ${qte}` : ""}`;
    }
    return "";
  });
  const [choosing, setChoosing] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [mes, setMes] = useState([]);
  const [durees, setDurees] = useState({ boutique: 30, telephone: 15, libre: 30 });
  const [actif, setActif] = useState(true);   // interrupteur admin (Paramètres)
  const [fermeture, setFermeture] = useState(null); // note de fermeture du jour choisi

  // Profil complet = champs du compte OU une adresse complète dans le carnet
  const adresseOk = (user?.addresses || []).some((a) => a.address1 && a.city && a.province && a.postal_code);
  const profilManquant = user && !(user.first_name && (user.phone || "")
    && ((user.address1 && user.city && user.province && user.postal_code) || adresseOk));
  // Libellé affiché d'un rendez-vous (type libre ou ancien type fixe)
  const labelDe = (r) => r.type === "libre" ? (r.type_label || t("rdv.kicker"))
    : (r.type === "boutique" ? t("rdv.type_boutique") : t("rdv.type_tel"));

  const chargeMes = () => {
    if (!user) return;
    api.get("/rendezvous/mes").then((r) => setMes(r.data.rendezvous || [])).catch(() => {});
  };
  useEffect(chargeMes, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/settings/shop").then((r) => {
      const d = r.data?.rdv_durees;
      if (d) setDurees((x) => ({ ...x, ...d }));
      if (r.data?.rdv_actif === false) setActif(false);
    }).catch(() => {});
  }, []);

  const choisirDate = async (d) => {
    setDate(d); setSlots(null); setFermeture(null);
    if (!d) { setSlotIso(""); return; }
    setChoosing(true);
    try {
      const { data } = await api.get("/rendezvous/disponibilites",
        { params: { date: d, type: "libre" } });
      setDuree(data.duree || durees.libre || 30);
      setSlots(data.ouvert ? data.creneaux : []);
      if (!data.ouvert && data.raison === "fermeture") setFermeture(data.note || "");
      // Changer de date : on GARDE le créneau choisi s'il est encore libre
      setSlotIso((cur) => (cur && (data.creneaux || []).some(
        (c) => c.iso === cur && c.statut === "libre") ? cur : ""));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSlots([]); setSlotIso(""); }
    finally { setChoosing(false); }
  };

  const reserver = async () => {
    if (!typeLabel.trim() || !slotIso) return;
    setSending(true);
    try {
      const { data } = await api.post("/rendezvous",
        { type: "libre", type_label: typeLabel.trim(), debut: slotIso, sujet });
      setDone(data.rendezvous);
      chargeMes();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const annuler = async (id) => {
    try {
      await api.post(`/rendezvous/${id}/annuler`);
      toast.success(t("rdv.cancelled"));
      chargeMes();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const auj = new Date().toLocaleDateString("en-CA");
  const max = new Date(Date.now() + 60 * 86400000).toLocaleDateString("en-CA");
  const aVenir = mes.filter((r) => r.statut === "confirme" && r.debut >= new Date().toISOString());

  const fmtQuand = (iso) => new Date(iso).toLocaleString(lang === "en" ? "en-CA" : "fr-CA",
    { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

  // Le client VOIT la grille publique complète : ses propres rendez-vous sont
  // marqués « Votre rendez-vous », les autres simplement « Occupé » (aucun détail).
  const estMien = (iso) => {
    const d0 = new Date(iso).getTime();
    const d1 = d0 + duree * 60000;
    return mes.some((r) => r.statut === "confirme"
      && new Date(r.debut).getTime() < d1 && new Date(r.fin).getTime() > d0);
  };

  return (
    <div data-testid="rdv-formulaire">
      {!user && (
        <FadeUp delay={0.1}>
          <div className="bg-surface border border-gold/40 rounded-2xl p-8 sm:p-10 text-center" data-testid="rdv-compte-requis">
            <CalendarCheck className="mx-auto h-10 w-10 text-gold" />
            <h2 className="mt-5 font-serif text-2xl text-ivory">{t("rdv.need_account_title")}</h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-ivory/60">{t("rdv.need_account_sub")}</p>
            <div className="mt-7 flex flex-wrap justify-center gap-4">
              {/* Retour au calendrier après connexion/création (state.next) —
                  les paramètres d'URL (ex. estimé produit) sont conservés */}
              <Link to="/inscription" state={{ next: location.pathname + location.search }} data-testid="rdv-inscription-btn"
                className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-8 py-3.5 rounded-full hover:shadow-[0_0_24px_rgba(212,175,55,0.4)] transition-all duration-300">
                {t("rdv.register")}
              </Link>
              <Link to="/connexion" state={{ next: location.pathname + location.search }} data-testid="rdv-connexion-btn"
                className="inline-flex items-center gap-2 border border-gold/40 text-gold px-8 py-3.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300">
                {t("rdv.login")}
              </Link>
            </div>
          </div>
        </FadeUp>
      )}

      {user && !actif && !done && (
        <FadeUp delay={0.1}>
          <div className="mt-8 rounded-xl border border-gold/50 bg-gold/10 p-6 text-sm text-gold" data-testid="rdv-inactif">
            {t("rdv.ferme_inactif")}
          </div>
        </FadeUp>
      )}

      {user && actif && !done && (
        <FadeUp delay={0.1}>
          {profilManquant && (
            <div className="rounded-xl border border-gold/50 bg-gold/10 p-5 text-sm text-gold" data-testid="rdv-profil-incomplet">
              {t("rdv.complete_profile")}{" "}
              {/* Retour au calendrier après l'enregistrement du profil */}
              <Link to="/compte" state={{ next: location.pathname + location.search }}
                className="font-bold underline" data-testid="rdv-complete-link">{t("rdv.complete_profile_link")}</Link>
            </div>
          )}

          {/* 1 — Type de rendez-vous : case de texte LIBRE (demande du propriétaire) */}
          <div className="mt-8" data-testid="rdv-etape-type">
            <h2 className="font-serif text-2xl text-ivory">{t("rdv.type_title")}</h2>
            <div className="mt-4">
              <input type="text" value={typeLabel} maxLength={80} required
                data-testid="rdv-type-libre-input"
                onChange={(e) => setTypeLabel(e.target.value)}
                placeholder={t("rdv.type_libre_placeholder")}
                className={`${inputCls} max-w-xl`} />
              <p className="mt-2 text-xs text-ivory/40">{durees.libre} min — {t("rdv.type_libre_hint")}</p>
            </div>
          </div>

          {/* 2 — Date */}
          <div className="mt-8" data-testid="rdv-etape-date">
            <h2 className="font-serif text-2xl text-ivory">{t("rdv.date_title")}</h2>
            <input type="date" min={auj} max={max} value={date} data-testid="rdv-date-input"
              onChange={(e) => choisirDate(e.target.value)} className={`mt-4 max-w-xs ${inputCls}`} />
          </div>

          {/* 3 — Créneau */}
          {date && (
            <div className="mt-8" data-testid="rdv-etape-creneau">
              <h2 className="flex items-center gap-2 font-serif text-2xl text-ivory">
                <Clock className="h-5 w-5 text-gold" /> {t("rdv.slot_title")}
                <span className="text-sm font-normal text-ivory/40">({duree} min)</span>
              </h2>
              {choosing && <Loader2 className="mt-5 h-5 w-5 animate-spin text-gold" />}
              {!choosing && slots && slots.length === 0 && (
                <p className="mt-5 rounded-xl border border-ivory/15 bg-surface px-5 py-4 text-sm text-ivory/60" data-testid="rdv-aucun-creneau">
                  {fermeture !== null ? `${t("rdv.ferme_note")}${fermeture ? ` — ${fermeture}` : ""}` : t("rdv.no_slots")}
                </p>
              )}
              {!choosing && slots && slots.length > 0 && (
                <>
                  <div className="mt-5 flex flex-wrap items-center gap-4 text-[11px] text-ivory/50">
                    <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full border border-gold" /> {t("rdv.libre")}</span>
                    <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-ivory/20" /> {t("rdv.occupe")}</span>
                    <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-gold" /> {t("rdv.mien")}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2.5 sm:grid-cols-5" data-testid="rdv-creneaux">
                    {slots.map((s) => {
                      const mien = s.statut !== "libre" && estMien(s.iso);
                      const statut = mien ? "mien" : s.statut;
                      return (
                        <button key={s.iso} type="button" data-testid={`rdv-slot-${s.h}`}
                          onClick={() => statut === "libre" && setSlotIso(s.iso)}
                          disabled={statut !== "libre"}
                          className={`rounded-lg border px-2 py-2.5 text-sm font-medium transition-all duration-200 ${
                            statut === "mien"
                              ? "border-gold bg-gold/20 text-gold cursor-default"
                              : statut === "libre"
                                ? slotIso === s.iso
                                  ? "border-gold bg-gold text-ink shadow-[0_0_16px_rgba(212,175,55,0.35)]"
                                  : "border-ivory/15 bg-surface text-ivory hover:border-gold/60"
                                : "border-ivory/10 bg-ivory/5 text-ivory/30 cursor-not-allowed"}`}>
                          {s.h}
                          {statut === "occupe" && <span className="block text-[9px] uppercase tracking-wider">{t("rdv.occupe")}</span>}
                          {statut === "mien" && <span className="block text-[9px] uppercase tracking-wider">{t("rdv.mien_court")}</span>}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Confirmation — le type (case libre) est OBLIGATOIRE pour confirmer */}
          {slotIso && (
            <div className="mt-10 rounded-2xl border border-gold/40 bg-surface p-7" data-testid="rdv-resume">
              <p className="text-sm text-ivory/70">
                <strong className="text-ivory">{typeLabel.trim() || t("rdv.type_requis_court")}</strong>
                {" — "}{fmtQuand(slotIso)} ({duree} min)
              </p>
              {typeLabel.trim().length < 2 && (
                <p className="mt-3 text-xs font-semibold text-gold" data-testid="rdv-type-requis">{t("rdv.type_requis")}</p>
              )}
              <div className="mt-5">
                <label className="text-xs font-semibold uppercase tracking-wider text-ivory/50" htmlFor="rdv-sujet">{t("rdv.sujet_label")}</label>
                <textarea id="rdv-sujet" rows={2} maxLength={500} value={sujet} data-testid="rdv-sujet-input"
                  onChange={(e) => setSujet(e.target.value)} placeholder={t("rdv.sujet_placeholder")}
                  className={`mt-2 ${inputCls}`} />
              </div>
              <button type="button" onClick={reserver} disabled={sending || !!profilManquant || typeLabel.trim().length < 2} data-testid="rdv-confirmer-btn"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-sm font-bold text-ink transition-all duration-300 hover:shadow-[0_0_24px_rgba(212,175,55,0.4)] disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto sm:px-12">
                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                {sending ? t("rdv.confirming") : t("rdv.confirm_btn")}
              </button>
            </div>
          )}
        </FadeUp>
      )}

      {/* Succès */}
      {done && (
        <FadeUp>
          <div className="rounded-2xl border border-gold/50 bg-surface p-10 text-center" data-testid="rdv-succes">
            <CalendarCheck className="mx-auto h-12 w-12 text-gold" />
            <h2 className="mt-5 font-serif text-3xl text-ivory">{t("rdv.success_title")}</h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-ivory/60">{t("rdv.success_sub")}</p>
            <p className="mt-4 font-semibold text-gold">{done.type_label || ""} — {fmtQuand(done.debut)}</p>
            <button type="button" onClick={() => { setDone(null); setSlotIso(""); setTypeLabel(""); setDate(""); setSlots(null); setSujet(""); }}
              className="mt-7 rounded-full border border-gold/40 px-7 py-3 text-sm font-semibold text-gold transition-colors duration-300 hover:bg-gold hover:text-ink" data-testid="rdv-nouveau-btn">
              {t("rdv.another")}
            </button>
          </div>
        </FadeUp>
      )}

      {/* Mes rendez-vous */}
      {user && aVenir.length > 0 && !done && (
        <div className="mt-12" data-testid="rdv-mes">
          <h2 className="font-serif text-2xl text-ivory">{t("rdv.my_rdv")}</h2>
          <ul className="mt-5 space-y-3">
            {aVenir.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-ivory/15 bg-surface p-5" data-testid={`rdv-item-${r.id}`}>
                <div>
                  <p className="font-semibold text-ivory">{labelDe(r)}</p>
                  <p className="text-sm text-ivory/50">{fmtQuand(r.debut)}</p>
                </div>
                <button type="button" onClick={() => annuler(r.id)} data-testid={`rdv-annuler-${r.id}`}
                  className="flex items-center gap-1.5 rounded-full border border-red-400/40 px-4 py-2 text-xs font-semibold text-red-400 transition-colors hover:bg-red-400/10">
                  <XCircle className="h-4 w-4" /> {t("rdv.cancel")}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
